import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Mail, Key, Lock, CheckCircle, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: new password, 4: success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Ingresa tu correo electrónico');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });
      
      if (response.data.success) {
        toast.success('Si el correo está registrado, recibirás un código');
        setStep(2);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      toast.error('Ingresa el código de 6 dígitos');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/auth/verify-reset-code`, { email, code });
      
      if (response.data.success) {
        toast.success('Código verificado');
        setStep(3);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Código inválido o expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/auth/reset-password`, {
        email,
        code,
        new_password: newPassword
      });
      
      if (response.data.success) {
        toast.success('Contraseña actualizada exitosamente');
        setStep(4);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Error al actualizar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      toast.success('Código reenviado');
    } catch (error) {
      toast.error('Error al reenviar el código');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-serif text-3xl text-black mb-2">Recuperar Contraseña</h1>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-400">ATELIER — JPM</p>
        </div>

        <div className="bg-white border border-border/50 p-8">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                    step >= s
                      ? 'bg-black text-white'
                      : 'bg-zinc-100 text-zinc-400'
                  }`}
                >
                  {step > s ? '✓' : s}
                </div>
                {s < 3 && (
                  <div className={`w-8 h-0.5 ${step > s ? 'bg-black' : 'bg-zinc-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Email */}
          {step === 1 && (
            <form onSubmit={handleRequestCode}>
              <div className="mb-6">
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                  Correo Electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="w-full h-11 pl-10 pr-4 bg-zinc-50 border-0 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                    data-testid="reset-email-input"
                  />
                </div>
              </div>

              <p className="text-xs text-zinc-500 mb-6">
                Te enviaremos un código de verificación a tu correo para restablecer tu contraseña.
              </p>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-black text-white text-[11px] uppercase tracking-wider font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50"
                data-testid="request-code-button"
              >
                {loading ? 'Enviando...' : 'Enviar Código'}
              </button>
            </form>
          )}

          {/* Step 2: Code Verification */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode}>
              <div className="mb-6">
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                  Código de Verificación
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="w-full h-11 pl-10 pr-4 bg-zinc-50 border-0 text-sm text-center tracking-[0.5em] font-mono focus:outline-none focus:ring-2 focus:ring-black/10"
                    data-testid="reset-code-input"
                  />
                </div>
              </div>

              <p className="text-xs text-zinc-500 mb-4">
                Ingresa el código de 6 dígitos enviado a <strong>{email}</strong>
              </p>

              <button
                type="button"
                onClick={handleResendCode}
                disabled={loading}
                className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-black mb-6 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Reenviar código
              </button>

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full h-11 bg-black text-white text-[11px] uppercase tracking-wider font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50"
                data-testid="verify-code-button"
              >
                {loading ? 'Verificando...' : 'Verificar Código'}
              </button>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword}>
              <div className="mb-4">
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full h-11 pl-10 pr-4 bg-zinc-50 border-0 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                    data-testid="new-password-input"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                  Confirmar Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full h-11 pl-10 pr-4 bg-zinc-50 border-0 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-black text-white text-[11px] uppercase tracking-wider font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50"
                data-testid="reset-password-button"
              >
                {loading ? 'Guardando...' : 'Guardar Nueva Contraseña'}
              </button>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="text-center py-6">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h2 className="font-serif text-xl text-black mb-2">¡Contraseña Actualizada!</h2>
              <p className="text-sm text-zinc-500 mb-6">
                Ya puedes iniciar sesión con tu nueva contraseña.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full h-11 bg-black text-white text-[11px] uppercase tracking-wider font-medium hover:bg-zinc-800 transition-colors"
                data-testid="go-to-login-button"
              >
                Ir a Iniciar Sesión
              </button>
            </div>
          )}

          {/* Back to Login */}
          {step !== 4 && (
            <button
              onClick={() => step === 1 ? navigate('/login') : setStep(step - 1)}
              className="flex items-center gap-1.5 mt-6 text-xs text-zinc-500 hover:text-black transition-colors mx-auto"
              data-testid="back-button"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {step === 1 ? 'Volver al Login' : 'Volver'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
